import { base64ToBytes, hexToBytes, isValidHex, textToBytes } from '../encoding';

export type ByteInputMode = 'auto' | 'hex' | 'text' | 'base64' | 'file';

export interface ResolvedByteInput {
  bytes: Uint8Array;
  mode: Exclude<ByteInputMode, 'auto'> | 'text';
}

function isLikelyBase64(input: string): boolean {
  const clean = input.replace(/\s+/g, '');
  return (
    clean.length >= 8 &&
    clean.length % 4 !== 1 &&
    /^[A-Za-z0-9+/_-]+={0,2}$/.test(clean) &&
    (/[=+/_-]/.test(clean) || /[A-Z]/.test(clean))
  );
}

export function resolveInputBytes(
  input: string,
  mode: ByteInputMode,
  fileBytes: Uint8Array | null = null,
): ResolvedByteInput {
  if (mode === 'file') {
    if (!fileBytes) {
      throw new Error('Choose a file to inspect');
    }
    return { bytes: fileBytes, mode: 'file' };
  }

  if (mode === 'hex') {
    if (!isValidHex(input)) {
      throw new Error('Hex input must contain complete byte pairs');
    }
    return { bytes: hexToBytes(input), mode: 'hex' };
  }

  if (mode === 'base64') {
    if (!input.trim()) {
      return { bytes: new Uint8Array(), mode: 'base64' };
    }
    return { bytes: base64ToBytes(input), mode: 'base64' };
  }

  if (mode === 'text') {
    return { bytes: textToBytes(input), mode: 'text' };
  }

  if (!input.trim()) {
    return { bytes: new Uint8Array(), mode: 'text' };
  }
  if (isValidHex(input)) {
    return { bytes: hexToBytes(input), mode: 'hex' };
  }
  if (isLikelyBase64(input)) {
    try {
      return { bytes: base64ToBytes(input), mode: 'base64' };
    } catch {
      // Fall through to text when an auto-detected candidate is malformed.
    }
  }
  return { bytes: textToBytes(input), mode: 'text' };
}
